import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Space,
  Divider,
  Tag,
  Tooltip,
  Checkbox
} from 'antd';
import {
  MailOutlined,
  LockOutlined,
  LoginOutlined,
  SafetyCertificateFilled,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  KeyOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { FaTruckLoading } from 'react-icons/fa';
import { useNavigate, Link } from 'react-router-dom';
import { ApiServices } from '../../services/apiServices';
import { ApiConfig } from '../../services/ApiConfigs';
import { useDispatch } from 'react-redux';
import { setUserData } from '../../redux/actions/DataActions';
import store from '../../redux/store';
import { errorMsg, successMsg } from '../../utils/ShowMessage';
import { routes } from "../../utils/routes.config";
import { handleCheckAuth } from '../../utils/handleAuth';
import './login.css';

const { Title, Text, Paragraph } = Typography;

const Login = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    return localStorage.getItem('rememberMe') === 'true';
  });

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      form.setFieldsValue({ email: savedEmail });
    }
  }, [form]);

  useEffect(() => {
    const authUser = handleCheckAuth();
    if (authUser) {
      const userData = store.getState().UserData.userData;
      if (userData?.role === "superAdmin") {
        navigate(routes.SUPER_DASHBOARD);
      } else if (userData?.role === "franchise") {
        navigate(routes.FRANCHISE_DASHBOARD);
      } else if (userData?.role === "user" && userData?.hasActivePlan === false) {
        navigate(routes.SELECT_PLAN);
      } else {
        navigate(routes.DASHBOARD);
      }
    }
  }, [navigate]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const res = await ApiServices('post', ApiConfig.LOGIN, values);
      console.log(res.role, "login response..");

      if (res.role === "admin" || res.role === "user" || res.role === "superAdmin" || res.role === "franchise") {
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
          localStorage.setItem('rememberedEmail', values.email);
        } else {
          localStorage.removeItem('rememberMe');
          localStorage.removeItem('rememberedEmail');
        }
        dispatch(setUserData(res));
        successMsg("Welcome back! Login successful.");

        if (res.role === "superAdmin") {
          navigate(routes.SUPER_DASHBOARD);
        } else if (res.role === "franchise") {
          navigate(routes.FRANCHISE_DASHBOARD);
        } else if (res.role === "user") {
          if (res.hasActivePlan === false) {
            navigate(routes.SELECT_PLAN);
          } else {
            navigate(routes.DASHBOARD);
          }
        } else if (res.role === "admin") {
          navigate(routes.DASHBOARD);
        }
      } else {
        errorMsg("User not authorized. Please contact admin.");
      }
    } catch (error) {
      console.log(error);
      errorMsg(error?.message || "Invalid credentials. Please verify your email and password.");
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <div className="login-page">
      {/* Dynamic ambient backgrounds */}
      <div className="login-background">
        <div className="login-orb login-orb-1" />
        <div className="login-orb login-orb-2" />
        <div className="login-grid-pattern" />
      </div>

      {/* Top Navigation bar */}
      <div className="login-top-bar">
        <Link to={routes.HOME} className="back-to-home">
          <ArrowLeftOutlined /> Back to Home
        </Link>
        <Link to={routes.API_DOCUMENTATION} className="login-docs-link">
          API Documentation
        </Link>
      </div>

      <div className="login-container">
        {/* Main Card */}
        <Card className="login-card" bordered={false}>
          {/* Header & Logo */}
          <div className="login-header">
            <div className="login-logo-wrapper">
              <FaTruckLoading className="login-logo-icon" />
            </div>
            <div className="login-brand-tag-wrapper">
              <div className="login-brand-tag">
                <span className="brand-dot" />
                <span>Magecomp Delivery Platform</span>
              </div>
            </div>
            <Title level={2} className="login-title">
              Sign In to Your Workspace
            </Title>
            <Text className="login-subtitle">
              Enter your credentials to access dispatch, fleet tracking, and Shopify orders.
            </Text>
          </div>

          <Divider className="login-divider" />

          {/* Form */}
          <Form
            form={form}
            name="login"
            layout="vertical"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
            size="large"
            requiredMark={false}
            initialValues={{
              email: "",
              password: ""
            }}
          >
            <Form.Item
              name="email"
              label={<span className="login-field-label">Email Address</span>}
              rules={[
                {
                  required: true,
                  message: 'Please enter your email address'
                },
                {
                  type: 'email',
                  message: 'Please enter a valid email address'
                }
              ]}
            >
              <Input
                prefix={<MailOutlined className="login-input-icon" />}
                placeholder="name@company.com"
                className="login-input"
              />
            </Form.Item>

            <div className="password-header-row">
              <span className="login-field-label">Password</span>
              <Link to={routes.FORGOT_PASSWORD} className="forgot-password-link">
                Forgot Password?
              </Link>
            </div>
            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: 'Please enter your password'
                },
                {
                  min: 6,
                  message: 'Password must be at least 6 characters'
                }
              ]}
              style={{ marginBottom: 24 }}
            >
              <Input.Password
                prefix={<LockOutlined className="login-input-icon" />}
                placeholder="••••••••••••"
                className="login-input"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 16 }}>
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="remember-me-checkbox"
              >
                Remember me
              </Checkbox>
            </Form.Item>

            <Form.Item style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                icon={<LoginOutlined />}
                className="login-submit-button"
              >
                {loading ? 'Authenticating...' : 'Sign In to Dashboard'}
              </Button>
            </Form.Item>
          </Form>

          {/* Security & trust banner */}
          <div className="login-footer">
            <div className="login-security-badge">
              <SafetyCertificateFilled className="login-security-icon" />
              <span>TLS 256-bit Encrypted & OAuth Protected Session</span>
            </div>
          </div>
        </Card>

        {/* Card sub-footer */}
        <div className="login-page-subfooter">
          <span>Need help or onboarding assistance?</span>{' '}
          <Link to={routes.SUPPORT} className="subfooter-link">Contact Support</Link>
        </div>
      </div>
    </div>
  );
};

export default Login;

